import { useMemo } from 'react';
import GridLayout, { WidthProvider, type Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useBoardValues, useMetricCatalogue, type Card, type DashboardDefinition, type MetricDef } from '@/api/boards';
import { useClientScope } from '@/contexts/ClientScopeContext';
import { BoardCard, type CardScopeNote } from './BoardCard';
import { GRID_GAP, ROW_HEIGHT } from '@/lib/boards';

const Grid = WidthProvider(GridLayout);

/** The client a dashboard is showing: picker (header scope), all, or a fixed company. */
export function useBoardClient(def: DashboardDefinition | undefined): { companyId: number | null; name: string | null } {
  const { scope } = useClientScope();
  if (!def) return { companyId: null, name: null };
  if (def.client.mode === 'fixed') return { companyId: def.client.companyId ?? null, name: def.client.companyName ?? null };
  if (def.client.mode === 'picker' && scope) return { companyId: scope.companyId, name: scope.name };
  return { companyId: null, name: null };
}

/** Fetch every metric card's value in one request, on the dashboard's refresh interval. */
export function useBoardData(def: DashboardDefinition | undefined, enabled = true) {
  const catalogue = useMetricCatalogue();
  const defs = useMemo(() => new Map((catalogue.data?.metrics ?? []).map((m) => [m.id, m])), [catalogue.data]);
  const client = useBoardClient(def);
  const items = useMemo(() => (def?.cards ?? []).filter((c) => c.metric && defs.has(c.metric)).map((c) => ({
    key: c.id,
    metric: c.metric!,
    params: {
      // A card can pin its own client; otherwise it follows the dashboard's.
      companyId: c.params?.companyId !== undefined ? c.params.companyId : client.companyId,
      ...(c.params?.psaScope ? { psaScope: c.params.psaScope } : {}),
    },
  })), [def, defs, client.companyId]);
  const values = useBoardValues(items, def?.refreshSeconds ?? 300, enabled && !!catalogue.data);
  return { defs, client, values, catalogueLoading: catalogue.isLoading };
}

export function scopeNoteFor(card: Card, def: MetricDef | undefined, client: { companyId: number | null; name: string | null }, scopedApplied: boolean | undefined): CardScopeNote | null {
  if (!def) return null;
  const pinned = card.params?.companyId !== undefined;
  const companyId = pinned ? card.params!.companyId : client.companyId;
  if (companyId == null) return null;
  if (!def.scoped) return client.companyId != null ? { label: 'All clients', muted: true } : null;
  if (pinned && card.params?.companyId !== client.companyId) return { label: card.params?.companyName ?? 'Pinned client', muted: !scopedApplied };
  return null;
}

export function BoardGrid({ def, editing, display, rowHeight = ROW_HEIGHT, onLayout, onEditCard }: {
  def: DashboardDefinition;
  editing?: boolean;
  display?: boolean;
  rowHeight?: number;
  onLayout?: (cards: Card[]) => void;
  onEditCard?: (card: Card) => void;
}) {
  const { defs, client, values } = useBoardData(def);
  const layout: Layout[] = def.cards.map((c) => ({ i: c.id, x: c.x, y: c.y, w: c.w, h: c.h, minW: 2, minH: c.type === 'kpi' || c.type === 'clock' ? 1 : 2 }));

  const renderCard = (c: Card) => {
    const m = c.metric ? defs.get(c.metric) : undefined;
    const r = values.data?.values[c.id];
    return (
      <BoardCard card={c} def={m} result={r} display={display} editing={editing}
        scopeNote={scopeNoteFor(c, m, client, r?.scopedApplied)}
        onOpenSettings={onEditCard ? () => onEditCard(c) : undefined} />
    );
  };

  return (
    <>
      {/* Phones: cards stack in reading order (grid editing is desktop only). */}
      <div className="space-y-3 md:hidden">
        {[...def.cards].sort((a, b) => a.y - b.y || a.x - b.x).map((c) => (
          <div key={c.id} style={{ height: Math.max(2, c.h) * (ROW_HEIGHT - 8) }}>{renderCard(c)}</div>
        ))}
      </div>
      <div className="hidden md:block">
        <Grid
          className="board-grid"
          layout={layout}
          cols={12}
          rowHeight={rowHeight}
          margin={[GRID_GAP, GRID_GAP]}
          containerPadding={[0, 0]}
          isDraggable={!!editing}
          isResizable={!!editing}
          draggableHandle=".board-drag-handle"
          compactType="vertical"
          onLayoutChange={(l) => {
            if (!editing || !onLayout) return;
            const by = new Map(l.map((x) => [x.i, x]));
            const next = def.cards.map((c) => { const p = by.get(c.id); return p ? { ...c, x: p.x, y: p.y, w: p.w, h: p.h } : c; });
            if (next.some((c, i) => c.x !== def.cards[i].x || c.y !== def.cards[i].y || c.w !== def.cards[i].w || c.h !== def.cards[i].h)) onLayout(next);
          }}
        >
          {def.cards.map((c) => <div key={c.id}>{renderCard(c)}</div>)}
        </Grid>
      </div>
    </>
  );
}
